import {
  Component,
  DestroyRef,
  Injector,
  OnInit,
  computed,
  inject,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from 'environments/environment';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { setFavicon } from './core/utils/favicon.utils';
import { IconService, WindowService } from './core/services';
import { GlobalLoadingService } from './services/loading/global-loading.service';
import { AppUpdateBannerComponent } from './core/app-update-banner/app-update-banner.component';
import { AppUpdateService } from './services/app-update/app-update.service';
import { APIVersionService } from './services/api-version/api-version.service';
import { TranslationsUpdateService } from './core/transloco/translations-update.service';
import { SubscriptionAlertModalComponent } from './subscription-alert/subscription-alert-modal.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    SubscriptionAlertModalComponent,
    AppUpdateBannerComponent,
  ],
  providers: [WindowService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'om2-web-app';

  private iconService = inject(IconService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private readonly globalLoadingService = inject(GlobalLoadingService);
  private readonly injector = inject(Injector);
  private readonly apiVersion = inject(APIVersionService);
  private readonly appUpdate = inject(AppUpdateService);
  private readonly translationsUpdate = inject(TranslationsUpdateService);

  protected readonly isLoading = this.globalLoadingService.isLoading;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  protected readonly showSubscriptionAlert = computed(
    () => !this.currentUrl()?.startsWith('/engage/')
  );

  private readonly dynamicCSSTags = [
    'quill-core-styles.css',
    'quill-snow-styles.css',
  ];

  ngOnInit() {
    setFavicon(environment.faviconPath);
    this.iconService.registerIcons();

    this.insertDynamicCSS();
    this.setupLoadingListener();
    this.apiVersion.watchForDeploy();
    this.appUpdate.watch();
    this.translationsUpdate.watch();
  }

  insertDynamicCSS() {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (event): event is NavigationEnd =>
            event instanceof NavigationEnd && !this.isInitialScreen(event.url)
        ),
        map(event => this.isInitialScreen(event.url))
      )
      .subscribe(isLoginPage => {
        this.manipulateDOMDynamicCSS(isLoginPage);
      });
  }

  isInitialScreen(url: string) {
    return (
      url.includes('/login') ||
      url.includes('/register') ||
      url.includes('/setup-account') ||
      url.includes('/re-activate') ||
      url.includes('/forgot-password') ||
      url.includes('/reset-password') ||
      url.startsWith('/engage/')
    );
  }

  manipulateDOMDynamicCSS(remove: boolean) {
    this.dynamicCSSTags.forEach(tag => {
      const link = document.querySelector(`link[href="${tag}"]`);
      if (link && !remove) {
        return;
      }
      if (remove) {
        link?.remove();
        return;
      }

      const newLink = document.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.href = tag;
      document.getElementsByTagName('head')[0].appendChild(newLink);
    });
  }

  setupLoadingListener() {
    if (typeof document === 'undefined') {
      return;
    }
    const loading$ = toObservable(this.globalLoadingService.isLoading, {
      injector: this.injector,
    });

    loading$
      .pipe(
        startWith(false),
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged()
      )
      .subscribe(isLoading => {
        if (isLoading) {
          document.body.classList.remove('is-loading');
        }
      });
  }
}
